import {
  YES_NO,
  applicantSection,
  feeInfo,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import {
  PB,
  SUPERVISION_ORGAN,
  allOf,
  every,
  exemptWhen,
  investorSection,
  propertySection,
  stampDutyExemptionFields,
} from "modules/ebiForms/definitions/sharedVehiclesConstruction";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E11. Zawiadomienie o zakończeniu budowy (PB-16, PB-16a) albo wniosek o pozwolenie na użytkowanie (PB-17) do PINB —
 * art. 54, 55 i 57 ustawy Prawo budowlane (t.j. Dz. U. 2025 poz. 418). Wzory GUNB (wersja 2026): PB-16
 * https://www.gov.pl/attachment/72adc31c-07e7-403b-9b42-3c01f007a892, PB-16a
 * https://www.gov.pl/attachment/735bab20-f370-4b8c-9d70-50958bb4313a, PB-17
 * https://www.gov.pl/attachment/32c35e05-82ee-411b-a7ff-42bacee077e1 — pobrane 2026-10-22 (DOCX): decyzja albo
 * zgłoszenie, informacja o obiekcie (PB-17), nieruchomość, lista załączników z art. 57 (PB-16a — skrócona, bez dziennika,
 * świadectwa energetycznego, przyłączy i audytu drogowego). Załączniki „o ile dotyczy” sterowane pytaniami; oświadczenia
 * inwestora z PB-16a (gdy budowę prowadzono bez kierownika) składane w formularzu. Opłata PB-17 — 25% stawek za
 * pozwolenie na budowę (według katalogu), kwotę potwierdza urząd.
 */
const PB16A: EbiCondition = { field: "rodzajPisma", equals: "pb16a" };
const PB17: EbiCondition = { field: "rodzajPisma", equals: "pb17" };
const CHOSEN: EbiCondition = { field: "rodzajPisma", filled: true };
const FULL: EbiCondition = { field: "rodzajPisma", in: ["pb16", "pb17"] };
const NO_MANAGER: EbiCondition = every(PB16A, { field: "kierownikBudowy", equals: "nie" });
const yes = (field: string): EbiCondition => ({ field, equals: "tak" });

const E11: IEbiFormDefinition = {
  code: "E11",
  version: 1,
  title: "Zawiadomienie o zakończeniu budowy albo wniosek o pozwolenie na użytkowanie",
  category: EEbiFormCategory.Construction,
  office: EEbiFormOffice.CountyUnit,
  documentType: "Pismo",
  description:
    "Zawiadomienie powiatowego inspektora nadzoru budowlanego o zakończeniu budowy (PB-16, dom jednorodzinny — PB-16a) albo wniosek o pozwolenie na użytkowanie (PB-17).",
  keywords: ["zakończenie budowy", "PB-16", "PB-16a", "pozwolenie na użytkowanie", "PB-17", "PINB", "nadzór budowlany", "odbiór"],
  organ: SUPERVISION_ORGAN,
  legalBasis: `art. 54, art. 55 i art. 57 ${PB}`,
  request: "Zawiadamiam o zakończeniu budowy albo wnoszę o udzielenie pozwolenia na użytkowanie obiektu budowlanego opisanego poniżej.",
  feeLabel: "wg stawek",
  fee: "Zawiadomienie o zakończeniu budowy (PB-16, PB-16a) — bez opłaty; pozwolenie na użytkowanie (PB-17) — 25% stawek opłaty skarbowej za pozwolenie na budowę, budownictwo mieszkaniowe zwolnione; pełnomocnictwo — 17 zł.",
  deadlineLabel: "14 dni na sprzeciw",
  deadline:
    "Zawiadomienie — organ może wnieść sprzeciw w terminie 14 dni od dnia doręczenia (art. 54 ust. 1 Prawa budowlanego); pozwolenie na użytkowanie — decyzja po obowiązkowej kontroli budowy.",
  variantField: "rodzajPisma",
  variants: {
    pb16: { title: "Zawiadomienie o zakończeniu budowy (PB-16)", documentType: "Pismo" },
    pb16a: { title: "Zawiadomienie o zakończeniu budowy budynku mieszkalnego jednorodzinnego (PB-16a)", documentType: "Pismo" },
    pb17: { title: "Wniosek o pozwolenie na użytkowanie (PB-17)", documentType: "Wniosek" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj pisma",
      fields: [
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          options: [
            { value: "pb16", label: "zawiadomienie o zakończeniu budowy (PB-16) — nie dotyczy budynków mieszkalnych jednorodzinnych" },
            { value: "pb16a", label: "zawiadomienie o zakończeniu budowy budynku mieszkalnego jednorodzinnego (PB-16a)" },
            { value: "pb17", label: "wniosek o pozwolenie na użytkowanie (PB-17)" },
          ],
        },
        feeInfo("infoRodzaj", "Zawiadomienie i wniosek możesz złożyć także w portalu e-Budownictwo."),
      ],
    },
    applicantSection(),
    representationSection(),
    investorSection(),
    {
      code: "decyzja",
      title: "Informacje dotyczące decyzji o pozwoleniu na budowę albo zgłoszenia budowy",
      visibleWhen: CHOSEN,
      fields: [
        { code: "decyzjaOrgan", type: "text", label: "Organ wydający decyzję albo przyjmujący zgłoszenie", required: true, maxLength: 300 },
        { code: "decyzjaData", type: "date", label: "Data wydania decyzji albo dokonania zgłoszenia", required: true, width: "half" },
        { code: "decyzjaNumer", type: "text", label: "Nr decyzji", width: "half", maxLength: 100 },
        { code: "decyzjaZnak", type: "text", label: "Znak sprawy", width: "half", maxLength: 100 },
      ],
    },
    {
      code: "obiekt",
      title: "Informacja o obiekcie",
      visibleWhen: PB17,
      fields: [
        {
          code: "obiektOpis",
          type: "textarea",
          label: "Funkcja (przeznaczenie) lub parametry obiektu, którego dotyczy wniosek o udzielenie pozwolenia na użytkowanie",
          required: true,
          maxLength: 3000,
        },
      ],
    },
    propertySection("Dane nieruchomości, na której znajduje się obiekt"),
    {
      code: "dokumentacja",
      title: "Dziennik budowy i dokumenty",
      description: "Odpowiedzi określają, które załączniki z art. 57 Prawa budowlanego trzeba dołączyć.",
      visibleWhen: CHOSEN,
      fields: [
        {
          code: "dziennik",
          type: "radio",
          label: "Dziennik budowy",
          required: true,
          visibleWhen: FULL,
          options: [
            { value: "elektroniczny", label: "prowadzony w systemie Elektroniczny Dziennik Budowy" },
            { value: "papierowy", label: "prowadzony w postaci papierowej" },
          ],
        },
        {
          code: "dziennikNumer",
          type: "text",
          label: "Indywidualny numer dziennika budowy (EDB)",
          required: true,
          visibleWhen: allOf(FULL, { field: "dziennik", equals: "elektroniczny" }),
          width: "half",
          maxLength: 50,
        },
        {
          ...feeInfo(
            "infoDziennik",
            "Oryginał papierowego dziennika budowy trzeba przekazać organowi — dostarcz go osobiście albo pocztą, podając numer wysłanego pisma.",
          ),
          visibleWhen: allOf(FULL, { field: "dziennik", equals: "papierowy" }),
        },
        {
          code: "kierownikBudowy",
          type: "radio",
          label: "Czy budowę prowadził kierownik budowy?",
          required: true,
          visibleWhen: PB16A,
          options: YES_NO,
        },
        {
          code: "swiadectwoWymagane",
          type: "radio",
          label: "Czy obiekt wymaga świadectwa charakterystyki energetycznej?",
          required: true,
          visibleWhen: FULL,
          options: [
            { value: "tak", label: "tak — obiekt jest budynkiem" },
            {
              value: "nie",
              label: "nie — obiekt nie jest budynkiem albo jest budynkiem wyłączonym (art. 3 ust. 4 ustawy o charakterystyce energetycznej budynków)",
            },
          ],
        },
        {
          code: "instalacjaGazowa",
          type: "radio",
          label: "Czy budynek ma instalację gazową?",
          required: true,
          visibleWhen: PB16A,
          options: YES_NO,
        },
        {
          code: "terenyPrzylegle",
          type: "radio",
          label: "Czy eksploatacja obiektu jest uzależniona od odpowiedniego zagospodarowania terenów przyległych?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "urzadzeniaUdt",
          type: "radio",
          label: "Czy w obiekcie są urządzenia techniczne wymagające decyzji zezwalającej na eksploatację (dozór techniczny)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "zaswiadczenie37i",
          type: "radio",
          label:
            "Czy wymagane jest zaświadczenie wójta, burmistrza albo prezydenta miasta potwierdzające spełnienie warunków z art. 37i ust. 8 ustawy o planowaniu i zagospodarowaniu przestrzennym?",
          required: true,
          defaultValue: "nie",
          visibleWhen: FULL,
          options: YES_NO,
        },
        {
          code: "droga",
          type: "radio",
          label:
            "Czy obiekt jest drogą lub jej odcinkiem, dla którego przedstawia się wynik audytu bezpieczeństwa ruchu drogowego (art. 24ga i 24l ustawy o drogach publicznych)?",
          required: true,
          defaultValue: "nie",
          visibleWhen: FULL,
          options: YES_NO,
        },
        {
          code: "zmianyNieistotne",
          type: "radio",
          label: "Czy w trakcie budowy wprowadzono zmiany, które nie odstępują w sposób istotny od zatwierdzonego projektu?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
        {
          code: "oswiadczeniaOrganow",
          type: "radio",
          label:
            "Czy wymagane są oświadczenia organów Państwowej Straży Pożarnej lub Państwowej Inspekcji Sanitarnej o braku sprzeciwu lub uwag (art. 56 ust. 1 Prawa budowlanego)?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "oswiadczeniaInwestora",
      title: "Oświadczenia inwestora (budowa bez kierownika budowy)",
      visibleWhen: NO_MANAGER,
      fields: [
        {
          code: "oswiadczenieZgodnosc",
          type: "declaration",
          label: "Oświadczam, że budynek wykonano zgodnie z projektem budowlanym oraz przepisami techniczno-budowlanymi.",
          required: true,
        },
        {
          code: "oswiadczeniePomiary",
          type: "declaration",
          label:
            "Oświadczam, że dokonałem (dokonałam) pomiarów powierzchni użytkowej budynku i poszczególnych lokali mieszkalnych w sposób zgodny z przepisami rozporządzenia wydanego na podstawie art. 34 ust. 6 pkt 1 Prawa budowlanego.",
          required: true,
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: PB17,
      fields: [
        feeInfo(
          "infoOplata",
          "Opłata skarbowa za pozwolenie na użytkowanie wynosi 25% stawek za pozwolenie na budowę — urząd potwierdzi kwotę. Sprawy budownictwa mieszkaniowego są zwolnione z opłaty.",
        ),
        ...stampDutyExemptionFields(),
      ],
    },
    replySection(),
  ],
  attachments: [
    { code: "projektTechniczny", label: "Projekt techniczny", visibleWhen: CHOSEN, required: true },
    {
      code: "swiadectwo",
      label: "Kopia świadectwa charakterystyki energetycznej budynku",
      hint: "Albo wydruk świadectwa przekazanego w postaci elektronicznej.",
      visibleWhen: allOf(FULL, yes("swiadectwoWymagane")),
      required: true,
    },
    {
      code: "oswiadczenieKierownika",
      label: "Oświadczenie kierownika budowy",
      hint: "O zgodności wykonania obiektu z projektem budowlanym lub warunkami pozwolenia na budowę oraz przepisami i o doprowadzeniu do należytego stanu i porządku terenu budowy (a w razie korzystania — także drogi, ulicy, sąsiedniej nieruchomości, budynku lub lokalu); przy PB-16a z informacją o pomiarach powierzchni użytkowej.",
      visibleWhen: { any: [FULL, every(PB16A, yes("kierownikBudowy"))] },
      required: true,
    },
    {
      code: "oswiadczenieTerenow",
      label: "Oświadczenie o właściwym zagospodarowaniu terenów przyległych",
      visibleWhen: allOf(CHOSEN, yes("terenyPrzylegle")),
      required: true,
    },
    {
      code: "protokoly",
      label: "Protokoły badań i sprawdzeń",
      hint: "Przyłączy i instalacji — wykonane przez osoby posiadające odpowiednie uprawnienia.",
      multiple: true,
      visibleWhen: FULL,
      required: true,
    },
    {
      code: "protokolGaz",
      label: "Protokoły badań szczelności instalacji gazowej",
      visibleWhen: every(PB16A, yes("instalacjaGazowa")),
      required: true,
    },
    {
      code: "decyzjaUdt",
      label: "Decyzja zezwalająca na eksploatację urządzenia technicznego (art. 14 ust. 1 ustawy o dozorze technicznym)",
      multiple: true,
      visibleWhen: allOf(CHOSEN, yes("urzadzeniaUdt")),
      required: true,
    },
    {
      code: "geodezja",
      label: "Dokumentacja geodezyjna z wynikami geodezyjnej inwentaryzacji powykonawczej, w tym mapą",
      hint: "Wraz z informacją o zgodności usytuowania obiektu z projektem zagospodarowania działki lub terenu albo o odstępstwach — sporządzona przez osobę z uprawnieniami w dziedzinie geodezji i kartografii.",
      visibleWhen: CHOSEN,
      required: true,
    },
    { code: "przylacza", label: "Potwierdzenie odbioru wykonanych przyłączy", multiple: true, visibleWhen: FULL, required: true },
    {
      code: "zaswiadczenieGminy",
      label: "Zaświadczenie wójta, burmistrza albo prezydenta miasta (art. 37i ust. 8 ustawy o planowaniu i zagospodarowaniu przestrzennym)",
      visibleWhen: allOf(FULL, yes("zaswiadczenie37i")),
      required: true,
    },
    {
      code: "audytBrd",
      label: "Wynik audytu bezpieczeństwa ruchu drogowego i uzasadnienie zarządcy drogi albo oświadczenie zarządcy drogi",
      hint: "Art. 24l ust. 1 i 4 ustawy o drogach publicznych — zależnie od kategorii drogi.",
      multiple: true,
      visibleWhen: allOf(FULL, yes("droga")),
      required: true,
    },
    {
      code: "rysunkiZmian",
      label: "Kopie rysunków z zatwierdzonego projektu z naniesionymi zmianami",
      hint: "W razie potrzeby z uzupełniającym opisem zmian.",
      multiple: true,
      visibleWhen: allOf(CHOSEN, yes("zmianyNieistotne")),
      required: true,
    },
    {
      code: "oswiadczeniaPspPis",
      label: "Oświadczenia organów Państwowej Straży Pożarnej lub Państwowej Inspekcji Sanitarnej o braku sprzeciwu lub uwag",
      multiple: true,
      visibleWhen: allOf(CHOSEN, yes("oswiadczeniaOrganow")),
      required: true,
    },
    ...proxyAttachments(),
    feeProofAttachment("Potwierdzenie uiszczenia opłaty skarbowej", { visibleWhen: every(PB17, { not: exemptWhen() }) }),
    otherAttachments(),
  ],
};

export default E11;
